/* eslint no-underscore-dangle: ["error", { "allow": ["__INITIAL_STATE__", "__STORE__"] }] */
import omit from 'lodash/omit';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getUrlParam } from 'Utils/Content';
import { configureAuthStore, isNonAnonymousUser, isAnonymousUser, getUserMvpdId, isMvpdUser } from 'Utils/UserCookie';
import { userInitialState } from 'State/User';
import { logger } from 'Utils/Logger';
import initialState from './initialState';

const isSSR = getUrlParam('isSSR', true);

const getDefaultState = (state) => {
  let defaultState = {};
  if (state) {
    const { plans, pageLayout, ...rest } = state;
    if (pageLayout && pageLayout.footerContent) {
      pageLayout.footerContent.isLoaded = true;
      defaultState.pageLayout = pageLayout;
    }
    // to always refetch plans on client
    if (plans) {
      plans.getPlansSuccess = false;
      plans.isFetchingPlans = false;
      defaultState.plans = plans;
    }

    defaultState = { ...rest, ...defaultState };
  }
  return defaultState;
};

const getAppConfig = () => {
  const screens = GLOBAL_SCREENS;
  const routes = GLOBAL_ROUTES;
  const strings = GLOBAL_STRINGS;
  const defaultConfig = GLOBAL_CONFIG;
  const pageMeta = GLOBAL_PAGEMETA;

  return { screens, strings, routes, defaultConfig, pageMeta };
};

const getInitialUserState = (config) => {
  try {
    // init auth store first time
    const token = configureAuthStore(config);
    const isAnonymous = isAnonymousUser();
    const isAuth = token && isNonAnonymousUser();
    if (isAuth) {
      // identity account type
      // remove accessToken and tokenExp before initial state.
      const user = omit(token, ['accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      logger.setAttr('profile_id', user.profileId);
      return {
        ...userInitialState,
        isAuthenticated: true,
        ...user,
      };
    }
    if (isAnonymous) {
      const user = omit(token, ['profileId', 'accessToken', 'tokenExpiration', 'accessTokenExpiration']);
      const { preauthorizedResources = [] } = user;
      const hasAuthorizedResources = preauthorizedResources.includes(config.defaultConfig.entitlementId);
      const hasMvpd = hasAuthorizedResources && isMvpdUser();
      const mvpdId = (hasMvpd && getUserMvpdId()) || '';
      return {
        ...userInitialState,
        isAnonymousUser: true,
        hasMvpd,
        mvpdId,
        ...user,
      };
    }
  } catch (e) {
    console.log(e);
  }
  return undefined;
};

const getInitialState = (state = {}) => {
  const defaultState = getDefaultState(state);
  const appConfig = getAppConfig();
  const user = getInitialUserState(appConfig);
  if (user) defaultState.user = user;
  return { ...defaultState, appConfig };
};
const {
  defaultConfig: { appEnv },
} = getAppConfig();
// NOOP in prod/staging
const wrappedDevtools =
  appEnv && ['qa', 'development'].includes(appEnv) ? devtools : (getInitialState) => getInitialState;

// to be invoked once per app lifecycle
const configureStore = () => {
  const additionalInitialState = getInitialState(window.__INITIAL_STATE__);

  return create(wrappedDevtools(() => ({ ...initialState, ...additionalInitialState })));
};

export let store = configureStore();

// for use in tests only
export const createMockStore = (customInitialState = {}) => {
  const additionalInitialState = getInitialState(window.__INITIAL_STATE__);

  const mockInitialState = {
    purchaseMode: {
      name: 'NEW_PLAN',
      isChangePlan: false,
      getPlansOnWelcome: true,
      showPlanDescOnPaymentReview: false,
      isPlanPayment: true,
      requirePlanSelection: true,
    },
    user: { isAuthenticated: true, email: 'test@fox.com' },
  };

  const combinedState = { ...additionalInitialState, ...mockInitialState, ...customInitialState };

  store = create(() => ({ ...initialState, ...combinedState }));
};

if (isSSR) {
  // required for server side rendering
  window.__STORE__ = store;
}
